import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getCategories, deleteCategory, exportCategories, importCategories } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import ImportExportBar from "../components/UI/ImportExportBar";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { I18nContext, useTranslation } from "../context/I18nContext";

export default function Categories() {
  // Category names can come back translated per the signed-in user's own
  // language (see ResolveEntityNames in entity_translation_handler.go) —
  // that resolution happens server-side at fetch time, so this list needs
  // to re-fetch whenever languageCode changes, not just on mount, or a
  // language switch would silently keep showing whatever names were
  // resolved under the PREVIOUS language until the next full page reload.
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("categories");
  const { t: tCommon } = useTranslation("common");
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchCategories = async () => {
    try {
      const data = await getCategories(token);
      setCategories(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchCategories(); }, [languageCode]);

  const categoryById = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[c.id] = c; });
    return map;
  }, [categories]);

  const sortedAndFiltered = useMemo(() => {
    let result = [...categories].filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [categories, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteCategory(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Category deleted successfully"));
      fetchCategories();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete category"));
    }
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    {
      key: "name",
      label: t("col_name", "Category"),
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <ICONS.Categories className="w-4 h-4" />
          </div>
          {row.name}
        </div>
      ),
    },
    {
      key: "parent_id",
      label: t("col_parent", "Parent"),
      className: "text-gray-600",
      render: (row) => (row.parent_id ? categoryById[row.parent_id]?.name || `#${row.parent_id}` : t("parent_top_level", "— (top level)")),
    },
    {
      key: "is_active",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
          {row.is_active ? tCommon("status_active", "Active") : tCommon("status_inactive", "Inactive")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Category")}
        message={t("delete_confirm_message", "Are you sure? This will fail if the category still has subcategories or products.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Categories size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Categories")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Group products — flat or in a hierarchy")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder={t("search_placeholder", "Search categories...")}
          />
          <ImportExportBar
            entityLabel="Categories"
            fileBaseName="categories"
            onExport={() => exportCategories(token)}
            onImport={(file) => importCategories(token, file)}
            onImported={fetchCategories}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-category")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_category", "Add Category")}
          </Button>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-category/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">#{row.id}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
